/**
 * Utility functions for Reviews components
 */

/**
 * Review URLs by locale
 */
export const REVIEW_URLS: Record<string, string> = {
  "nl-nl": "https://www.trstd.com/nl-nl/reviews/wovar-nl",
  "nl-be": "https://www.trstd.com/nl-be/reviews/wovar-be",
  "de-de":
    "https://www.trustedshops.de/bewertung/info_X192B860A5A84E9BCE9EF468F61CECDFE.html",
  "fr-fr": "https://www.trstd.com/fr-fr/reviews/wovar-fr",
};

const DEFAULT_REVIEW_URL = REVIEW_URLS["nl-nl"];

/**
 * Gets the review URL for a locale
 * Handles both underscore ("nl_NL") and dash ("nl-nl") formats
 * Also handles partial locales like "de" -> "de-de"
 */
export function getReviewUrl(locale: string): string {
  if (!locale) return DEFAULT_REVIEW_URL;

  // Robust normalization: Replace ALL underscores with dashes, then lowercase
  // Handles: "nl_NL", "NL_NL", "nl-nl", "NL-NL", "nl_nl", etc.
  // Pattern consistent with eTrusted config.ts normalization
  const normalized = locale.replace(/_/g, "-").toLowerCase();

  // Try exact match first
  if (REVIEW_URLS[normalized]) {
    return REVIEW_URLS[normalized];
  }

  // If no exact match, try to match by language prefix
  // e.g., "de" should match "de-de", "fr" should match "fr-fr"
  const languagePrefix = normalized.split("-")[0];
  const matchedKey = Object.keys(REVIEW_URLS).find((key) =>
    key.startsWith(languagePrefix)
  );

  return matchedKey ? REVIEW_URLS[matchedKey] : DEFAULT_REVIEW_URL;
}

/**
 * Formats a rating number based on the current locale
 * @param rating - The rating value to format
 * @param locale - The locale string (e.g., 'nl-nl', 'de-de', 'en-gb')
 * @returns Formatted rating string with 1 decimal place
 */
export function formatRating(rating: number, locale: string): string {
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  return formatter.format(rating);
}

/**
 * Rounds a review count down to the nearest configured step.
 * Example: 9357 with step 250 => 9250.
 */
export function roundReviewCountDown(reviewCount: number, step = 250): number {
  if (reviewCount <= 0 || step <= 0) {
    return 0;
  }

  return Math.floor(reviewCount / step) * step;
}

/**
 * Formats a review count for stepped banner display.
 * Example: 9357 => "9,250+" for en-style locales, locale-aware.
 */
export function formatSteppedReviewCount(
  reviewCount: number,
  locale = "nl-nl",
  step = 250
): string {
  const roundedReviewCount = roundReviewCountDown(reviewCount, step);

  return `${roundedReviewCount.toLocaleString(locale)}+`;
}

/**
 * Formats customer name based on locale
 * NL/BE: First name + Last initial (e.g., "Piet B.")
 * FR/DE: First initial + Last name (e.g., "P. Boer")
 * @returns null if name is empty/invalid (caller should show anonymous label)
 */
export function formatCustomerName(
  name: string | undefined,
  locale: string
): string | null {
  if (!name?.trim()) return null;

  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) {
    // Single name only - just return it
    return parts[0];
  }

  const firstName = parts[0];
  const lastName = parts[parts.length - 1];

  // Normalize locale format (consistent with getReviewUrl)
  const normalized = locale ? locale.replace(/_/g, "-").toLowerCase() : "";

  // FR/DE: First initial + Last name
  if (normalized.startsWith("fr") || normalized.startsWith("de")) {
    return `${firstName.charAt(0)}. ${lastName}`;
  }

  // NL/BE (default): First name + Last initial
  return `${firstName} ${lastName.charAt(0)}.`;
}

/**
 * Formats a review date to a readable locale-aware string.
 * Returns the original value if the input cannot be parsed as a date.
 */
export function formatReviewDate(dateString: string, locale: string): string {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return dateString;
  }

  return date.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
